import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { 
  FileText, Search, Eye, Store, Truck, RefreshCw, Printer, 
  CheckCircle, Loader2, Package, Send, ClipboardList, FileCheck,
  AlertTriangle, Building2, MapPin, Calendar, ArrowRight
} from 'lucide-react';
import { formatDate } from '@/utils/dateFormat';
import api from '@/services/api';

const STATUS_CONFIG = {
  approved: { label: 'Approved', color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
  invoiced: { label: 'Invoiced', color: 'bg-purple-100 text-purple-700', icon: FileText },
  dispatched: { label: 'Dispatched', color: 'bg-amber-100 text-amber-700', icon: Truck },
  delivered: { label: 'Delivered', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  received: { label: 'Received', color: 'bg-green-100 text-green-700', icon: Package },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700', icon: CheckCircle }
};

const FranchiseSupplyPage = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [activeTab, setActiveTab] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [transferOrders, setTransferOrders] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Dialog states
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [showDispatchDialog, setShowDispatchDialog] = useState(false);
  const [showPackingDialog, setShowPackingDialog] = useState(false);
  
  // Invoice form
  const [invoiceForm, setInvoiceForm] = useState({
    invoice_number: '',
    invoice_date: new Date().toISOString().split('T')[0],
    notes: ''
  });
  
  // Dispatch form
  const [dispatchForm, setDispatchForm] = useState({
    vehicle_number: '',
    driver_name: '',
    driver_phone: '',
    transporter_name: '',
    lr_number: '',
    expected_delivery_date: '',
    dispatch_notes: ''
  });
  
  const [processing, setProcessing] = useState(false);

  // Fetch transfer orders assigned to this store as source
  const fetchTransferOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = { 
        source_store_id: user?.store_id || user?.entity_id,
        page_size: 100 
      };
      if (statusFilter) params.status = statusFilter;
      if (searchQuery) params.search = searchQuery;
      
      const response = await api.get('/store/franchise-supply/transfer-orders', { params });
      setTransferOrders(response.data?.items || response.data || []);
    } catch (error) {
      console.error('Error fetching transfer orders:', error);
      toast({ title: 'Error', description: 'Failed to fetch transfer orders', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [user?.store_id, user?.entity_id, statusFilter, searchQuery, toast]);

  useEffect(() => {
    fetchTransferOrders();
  }, [fetchTransferOrders]);

  // View order details
  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setShowDetailsDialog(true);
  };

  // Open invoice dialog
  const handleOpenInvoiceDialog = (order) => {
    setSelectedOrder(order);
    // Generate invoice number based on store code
    const date = new Date();
    const prefix = user?.store_code || user?.entity_code || 'STO';
    const year = date.getFullYear();
    const sequence = String(Math.floor(Math.random() * 9999) + 1).padStart(4, '0');
    setInvoiceForm({
      invoice_number: `${prefix}-INV-${year}-${sequence}`,
      invoice_date: date.toISOString().split('T')[0],
      notes: ''
    });
    setShowInvoiceDialog(true);
  };

  // Create invoice
  const handleCreateInvoice = async () => {
    if (!invoiceForm.invoice_number) {
      toast({ title: 'Error', description: 'Invoice number is required', variant: 'destructive' });
      return;
    }

    setProcessing(true);
    try {
      await api.post(`/store/franchise-supply/transfer-orders/${selectedOrder.id}/create-invoice`, {
        invoice_number: invoiceForm.invoice_number,
        invoice_date: invoiceForm.invoice_date,
        notes: invoiceForm.notes
      });
      
      toast({ title: 'Success', description: `Invoice ${invoiceForm.invoice_number} created successfully` });
      setShowInvoiceDialog(false);
      fetchTransferOrders();
    } catch (error) {
      console.error('Error creating invoice:', error);
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to create invoice', 
        variant: 'destructive' 
      });
    } finally {
      setProcessing(false);
    }
  };

  // Open dispatch dialog
  const handleOpenDispatchDialog = (order) => {
    setSelectedOrder(order);
    setDispatchForm({
      vehicle_number: '',
      driver_name: '',
      driver_phone: '',
      transporter_name: '',
      lr_number: '',
      expected_delivery_date: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      dispatch_notes: ''
    });
    setShowDispatchDialog(true);
  };

  // Dispatch order
  const handleDispatch = async () => {
    if (!dispatchForm.vehicle_number) {
      toast({ title: 'Error', description: 'Vehicle number is required', variant: 'destructive' });
      return;
    }

    setProcessing(true);
    try {
      await api.post(`/store/franchise-supply/transfer-orders/${selectedOrder.id}/dispatch`, dispatchForm);
      
      toast({ title: 'Success', description: 'Order dispatched successfully' });
      setShowDispatchDialog(false);
      fetchTransferOrders();
    } catch (error) {
      console.error('Error dispatching:', error);
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to dispatch order', 
        variant: 'destructive' 
      });
    } finally {
      setProcessing(false);
    }
  };

  // Print packing list
  const handlePrintPackingList = (order) => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Packing List - ${order.transfer_number}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; max-width: 800px; margin: 0 auto; }
            .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #333; padding-bottom: 15px; }
            .header h1 { margin: 0; font-size: 24px; }
            .header h2 { margin: 5px 0; font-size: 14px; color: #666; }
            .parties { display: flex; justify-content: space-between; margin: 20px 0; }
            .party-box { width: 45%; padding: 15px; border: 1px solid #ddd; border-radius: 8px; }
            .party-box h4 { margin: 0 0 10px; font-size: 12px; color: #888; text-transform: uppercase; }
            .party-box p { margin: 3px 0; font-size: 12px; }
            .party-box .name { font-size: 14px; font-weight: 600; }
            table { width: 100%; border-collapse: collapse; margin: 20px 0; }
            th, td { border: 1px solid #ddd; padding: 10px; text-align: left; font-size: 12px; }
            th { background-color: #f5f5f5; font-weight: 600; }
            .text-right { text-align: right; }
            .text-center { text-align: center; }
            .checkbox { width: 20px; height: 20px; border: 1px solid #333; display: inline-block; }
            .footer { margin-top: 40px; display: flex; justify-content: space-between; }
            .signature { text-align: center; width: 200px; }
            .signature-line { border-top: 1px solid #333; margin-top: 50px; padding-top: 5px; font-size: 12px; }
            .totals { background: #f9f9f9; padding: 15px; margin-top: 20px; border-radius: 8px; }
            @media print { body { padding: 0; } }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>PACKING LIST</h1>
            <h2>Transfer Order: ${order.transfer_number}</h2>
            <p style="margin: 5px 0; font-size: 12px;">Date: ${new Date().toLocaleDateString('en-IN')}</p>
          </div>
          
          <div class="parties">
            <div class="party-box">
              <h4>From (Source)</h4>
              <p class="name">${order.source_name}</p>
              <p>${order.source_code || ''}</p>
              ${order.source_gstin ? `<p>GSTIN: ${order.source_gstin}</p>` : ''}
              ${order.source_address ? `<p>${order.source_address.address_line1 || ''}, ${order.source_address.district || order.source_address.city || ''}</p>` : ''}
            </div>
            <div class="party-box">
              <h4>To (Destination)</h4>
              <p class="name">${order.destination_name}</p>
              <p>${order.destination_code || ''}</p>
              ${order.destination_gstin ? `<p>GSTIN: ${order.destination_gstin}</p>` : ''}
              ${order.destination_address ? `<p>${order.destination_address.address_line1 || ''}, ${order.destination_address.district || order.destination_address.city || ''}</p>` : ''}
            </div>
          </div>
          
          <table>
            <thead>
              <tr>
                <th style="width: 40px">#</th>
                <th>Product Description</th>
                <th>SKU</th>
                <th>HSN</th>
                <th class="text-center">Qty</th>
                <th class="text-center">Packed</th>
                <th class="text-center">Check</th>
              </tr>
            </thead>
            <tbody>
              ${order.items?.map((item, idx) => `
                <tr>
                  <td class="text-center">${idx + 1}</td>
                  <td><strong>${item.product_name}</strong></td>
                  <td>${item.sku || '-'}</td>
                  <td>${item.hsn_code || '-'}</td>
                  <td class="text-center"><strong>${item.approved_qty || item.requested_qty}</strong></td>
                  <td class="text-center">______</td>
                  <td class="text-center"><span class="checkbox"></span></td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          
          <div class="totals">
            <strong>Total Items: ${order.items?.length || 0}</strong> | 
            <strong>Total Quantity: ${order.items?.reduce((sum, item) => sum + (item.approved_qty || item.requested_qty || 0), 0)}</strong>
          </div>
          
          <div style="margin-top: 30px; padding: 15px; border: 1px solid #ddd; border-radius: 8px;">
            <h4 style="margin: 0 0 10px;">Packing Notes:</h4>
            <div style="min-height: 60px; border-bottom: 1px dashed #ccc;"></div>
          </div>
          
          <div class="footer">
            <div class="signature">
              <div class="signature-line">Packed By</div>
            </div>
            <div class="signature">
              <div class="signature-line">Checked By</div>
            </div>
            <div class="signature">
              <div class="signature-line">Dispatched By</div>
            </div>
          </div>
          
          <script>window.print();</script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Print invoice
  const handlePrintInvoice = (order) => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Tax Invoice - ${order.invoice_info?.invoice_number || order.transfer_number}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; max-width: 800px; margin: 0 auto; }
            .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #333; padding-bottom: 15px; }
            .header h1 { margin: 0; font-size: 24px; }
            .parties { display: flex; justify-content: space-between; margin: 20px 0; }
            .party-box { width: 45%; padding: 15px; border: 1px solid #ddd; border-radius: 8px; }
            .party-box h4 { margin: 0 0 10px; font-size: 12px; color: #888; text-transform: uppercase; }
            .party-box p { margin: 3px 0; font-size: 12px; }
            .party-box .name { font-size: 14px; font-weight: 600; }
            table { width: 100%; border-collapse: collapse; margin: 20px 0; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 12px; }
            th { background-color: #f5f5f5; font-weight: 600; }
            .text-right { text-align: right; }
            .totals { width: 250px; margin-left: auto; margin-top: 20px; }
            .totals tr td { border: none; padding: 5px 10px; }
            .totals .grand-total { font-size: 16px; font-weight: bold; border-top: 2px solid #333 !important; }
            .footer { margin-top: 40px; display: flex; justify-content: space-between; }
            .signature { text-align: center; width: 200px; }
            .signature-line { border-top: 1px solid #333; margin-top: 50px; padding-top: 5px; font-size: 12px; }
            @media print { body { padding: 0; } }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>TAX INVOICE</h1>
            <p style="font-size: 14px; margin: 5px 0;">${order.invoice_info?.invoice_number || 'DRAFT'}</p>
            <p style="font-size: 12px; color: #666;">
              Invoice Date: ${order.invoice_info?.invoice_date ? new Date(order.invoice_info.invoice_date).toLocaleDateString('en-IN') : new Date().toLocaleDateString('en-IN')}
            </p>
            <p style="font-size: 11px; color: #888;">Transfer Order: ${order.transfer_number}</p>
          </div>
          
          <div class="parties">
            <div class="party-box">
              <h4>From (Seller)</h4>
              <p class="name">${order.source_name}</p>
              <p>${order.source_code || ''}</p>
              ${order.source_gstin ? `<p><strong>GSTIN:</strong> ${order.source_gstin}</p>` : ''}
              ${order.source_address ? `<p>${order.source_address.address_line1 || ''}</p><p>${order.source_address.district || ''}, ${order.source_address.state || ''} - ${order.source_address.pincode || ''}</p>` : ''}
            </div>
            <div class="party-box">
              <h4>To (Buyer)</h4>
              <p class="name">${order.destination_name}</p>
              <p>${order.destination_code || ''}</p>
              ${order.destination_gstin ? `<p><strong>GSTIN:</strong> ${order.destination_gstin}</p>` : ''}
              ${order.destination_address ? `<p>${order.destination_address.address_line1 || ''}</p><p>${order.destination_address.district || ''}, ${order.destination_address.state || ''} - ${order.destination_address.pincode || ''}</p>` : ''}
            </div>
          </div>
          
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Description</th>
                <th>HSN</th>
                <th class="text-right">Qty</th>
                <th class="text-right">Rate</th>
                <th class="text-right">Tax%</th>
                <th class="text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              ${order.items?.map((item, idx) => {
                const qty = item.approved_qty || item.requested_qty || 0;
                const rate = item.transfer_price || item.cost_price || 0;
                const taxRate = item.tax_rate || 0;
                const subtotal = qty * rate;
                const tax = subtotal * (taxRate / 100);
                const total = subtotal + tax;
                return `
                  <tr>
                    <td>${idx + 1}</td>
                    <td><strong>${item.product_name}</strong><br/><small style="color:#666">${item.sku || ''}</small></td>
                    <td>${item.hsn_code || '-'}</td>
                    <td class="text-right">${qty}</td>
                    <td class="text-right">₹${rate.toFixed(2)}</td>
                    <td class="text-right">${taxRate}%</td>
                    <td class="text-right">₹${total.toFixed(2)}</td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
          
          <table class="totals">
            <tr>
              <td>Subtotal:</td>
              <td class="text-right">₹${(order.subtotal || 0).toFixed(2)}</td>
            </tr>
            <tr>
              <td>Tax:</td>
              <td class="text-right">₹${(order.total_tax || 0).toFixed(2)}</td>
            </tr>
            <tr class="grand-total">
              <td>Grand Total:</td>
              <td class="text-right">₹${(order.grand_total || 0).toFixed(2)}</td>
            </tr>
          </table>
          
          ${order.is_interstate ? `
            <div style="margin-top: 20px; padding: 10px; background: #fff3cd; border-radius: 4px; font-size: 11px;">
              <strong>Interstate Supply:</strong> IGST applicable
            </div>
          ` : ''}
          
          <div class="footer">
            <div class="signature">
              <div class="signature-line">Prepared By</div>
            </div>
            <div class="signature">
              <div class="signature-line">Authorized Signatory</div>
            </div>
          </div>
          
          <script>window.print();</script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Filter orders by tab
  const getFilteredOrders = () => {
    switch (activeTab) {
      case 'pending':
        return transferOrders.filter(o => o.status === 'approved');
      case 'invoiced':
        return transferOrders.filter(o => o.status === 'invoiced');
      case 'dispatched':
        return transferOrders.filter(o => ['dispatched', 'delivered', 'received', 'completed'].includes(o.status));
      default:
        return transferOrders;
    }
  };

  const filteredOrders = getFilteredOrders();

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <Toaster />
      
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Truck className="w-6 h-6 text-teal-600" /> Franchise Supply
          </h1>
          <p className="text-gray-500">Process approved orders for franchise stores</p>
        </div>
        <Button variant="outline" onClick={fetchTransferOrders}>
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-600 text-sm">Pending Invoice</p>
                <p className="text-2xl font-bold text-blue-800">
                  {transferOrders.filter(o => o.status === 'approved').length}
                </p>
              </div>
              <FileText className="w-8 h-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-600 text-sm">Ready to Dispatch</p>
                <p className="text-2xl font-bold text-purple-800">
                  {transferOrders.filter(o => o.status === 'invoiced').length}
                </p>
              </div>
              <Package className="w-8 h-8 text-purple-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-amber-600 text-sm">In Transit</p>
                <p className="text-2xl font-bold text-amber-800">
                  {transferOrders.filter(o => o.status === 'dispatched').length}
                </p>
              </div>
              <Truck className="w-8 h-8 text-amber-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-600 text-sm">Completed</p>
                <p className="text-2xl font-bold text-emerald-800">
                  {transferOrders.filter(o => ['delivered', 'received', 'completed'].includes(o.status)).length}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-emerald-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-gray-100 mb-4">
          <TabsTrigger value="pending" className="data-[state=active]:bg-white">
            <FileText className="w-4 h-4 mr-2" /> Pending Invoice
          </TabsTrigger>
          <TabsTrigger value="invoiced" className="data-[state=active]:bg-white">
            <Package className="w-4 h-4 mr-2" /> Ready to Dispatch
          </TabsTrigger>
          <TabsTrigger value="dispatched" className="data-[state=active]:bg-white">
            <Truck className="w-4 h-4 mr-2" /> Dispatched
          </TabsTrigger>
          <TabsTrigger value="all" className="data-[state=active]:bg-white">
            All Orders
          </TabsTrigger>
        </TabsList>

        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="p-0">
            {loading ? (
              <div className="text-center py-12">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-teal-600" />
                <p className="text-gray-500 mt-2">Loading orders...</p>
              </div>
            ) : filteredOrders.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No orders found</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="font-semibold">Transfer Order</TableHead>
                    <TableHead className="font-semibold">Destination</TableHead>
                    <TableHead className="font-semibold">Items</TableHead>
                    <TableHead className="font-semibold">Date</TableHead>
                    <TableHead className="font-semibold">Status</TableHead>
                    <TableHead className="font-semibold text-right">Amount</TableHead>
                    <TableHead className="font-semibold text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredOrders.map(order => {
                    const statusConfig = STATUS_CONFIG[order.status] || STATUS_CONFIG.approved;
                    return (
                      <TableRow key={order.id} className="hover:bg-gray-50">
                        <TableCell>
                          <p className="font-medium text-gray-800">{order.transfer_number}</p>
                          {order.invoice_info?.invoice_number && (
                            <p className="text-xs text-purple-600">INV: {order.invoice_info.invoice_number}</p>
                          )}
                          {order.stock_request_number && (
                            <p className="text-xs text-gray-500">PO: {order.stock_request_number}</p>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Store className="w-4 h-4 text-gray-400" />
                            <div>
                              <p className="font-medium text-gray-700">{order.destination_name}</p>
                              <p className="text-xs text-gray-500">{order.destination_code}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{order.items?.length || 0} items</Badge>
                        </TableCell>
                        <TableCell className="text-gray-600 text-sm">
                          {formatDate(order.created_at)}
                        </TableCell>
                        <TableCell>
                          <Badge className={statusConfig.color}>
                            {statusConfig.label}
                          </Badge>
                          {order.is_interstate && (
                            <Badge variant="outline" className="ml-1 text-xs">Interstate</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-semibold text-gray-800">
                          ₹{(order.grand_total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-center gap-1">
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8"
                              onClick={() => handleViewDetails(order)}
                              title="View Details"
                            >
                              <Eye className="w-4 h-4 text-gray-500" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8"
                              onClick={() => handlePrintPackingList(order)}
                              title="Print Packing List"
                            >
                              <ClipboardList className="w-4 h-4 text-gray-500" />
                            </Button>
                            
                            {order.status === 'approved' && (
                              <Button 
                                variant="outline" 
                                size="sm"
                                className="h-8 text-purple-600 border-purple-300 hover:bg-purple-50"
                                onClick={() => handleOpenInvoiceDialog(order)}
                              >
                                <FileText className="w-3 h-3 mr-1" /> Invoice
                              </Button>
                            )}
                            
                            {order.status === 'invoiced' && (
                              <>
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  className="h-8 w-8"
                                  onClick={() => handlePrintInvoice(order)}
                                  title="Print Invoice"
                                >
                                  <Printer className="w-4 h-4 text-purple-500" />
                                </Button>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  className="h-8 text-amber-600 border-amber-300 hover:bg-amber-50"
                                  onClick={() => handleOpenDispatchDialog(order)}
                                >
                                  <Truck className="w-3 h-3 mr-1" /> Dispatch
                                </Button>
                              </>
                            )}
                            
                            {['dispatched', 'delivered', 'received'].includes(order.status) && (
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="h-8 w-8"
                                onClick={() => handlePrintInvoice(order)}
                                title="Print Invoice"
                              >
                                <Printer className="w-4 h-4 text-gray-500" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </Tabs>

      {/* View Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-teal-600" />
              Transfer Order - {selectedOrder?.transfer_number}
            </DialogTitle>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-4">
              {/* Source & Destination */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Building2 className="w-4 h-4 text-blue-600" />
                    <span className="text-xs text-blue-600 font-medium">FROM (Source)</span>
                  </div>
                  <p className="font-semibold text-gray-800">{selectedOrder.source_name}</p>
                  <p className="text-sm text-gray-600">{selectedOrder.source_code}</p>
                  {selectedOrder.source_gstin && (
                    <p className="text-xs text-gray-500 mt-1">GSTIN: {selectedOrder.source_gstin}</p>
                  )}
                </div>
                <div className="p-4 rounded-lg bg-purple-50 border border-purple-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Store className="w-4 h-4 text-purple-600" />
                    <span className="text-xs text-purple-600 font-medium">TO (Destination)</span>
                  </div>
                  <p className="font-semibold text-gray-800">{selectedOrder.destination_name}</p>
                  <p className="text-sm text-gray-600">{selectedOrder.destination_code}</p>
                  {selectedOrder.destination_gstin && (
                    <p className="text-xs text-gray-500 mt-1">GSTIN: {selectedOrder.destination_gstin}</p>
                  )}
                </div>
              </div>

              {/* Status & Info */}
              <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                <Badge className={STATUS_CONFIG[selectedOrder.status]?.color || 'bg-gray-100'}>
                  {STATUS_CONFIG[selectedOrder.status]?.label || selectedOrder.status}
                </Badge>
                {selectedOrder.invoice_info?.invoice_number && (
                  <span className="text-sm text-gray-600">
                    Invoice: <strong>{selectedOrder.invoice_info.invoice_number}</strong>
                  </span>
                )}
                {selectedOrder.is_interstate && (
                  <Badge variant="outline" className="text-orange-600 border-orange-300">
                    Interstate Transfer
                  </Badge>
                )}
              </div>

              {/* Items */}
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-100">
                    <TableHead>#</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead className="text-right">Rate</TableHead>
                    <TableHead className="text-right">Tax</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedOrder.items?.map((item, idx) => {
                    const qty = item.approved_qty || item.requested_qty || 0;
                    const rate = item.transfer_price || item.cost_price || 0;
                    const subtotal = qty * rate;
                    const tax = subtotal * ((item.tax_rate || 0) / 100);
                    return (
                      <TableRow key={idx}>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>
                          <p className="font-medium">{item.product_name}</p>
                          <p className="text-xs text-gray-500">{item.sku}</p>
                        </TableCell>
                        <TableCell className="text-right">{qty}</TableCell>
                        <TableCell className="text-right">₹{rate.toFixed(2)}</TableCell>
                        <TableCell className="text-right">{item.tax_rate || 0}%</TableCell>
                        <TableCell className="text-right font-medium">₹{(subtotal + tax).toFixed(2)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {/* Totals */}
              <div className="flex justify-end">
                <div className="w-64 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Subtotal:</span>
                    <span>₹{(selectedOrder.subtotal || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Tax:</span>
                    <span>₹{(selectedOrder.total_tax || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between font-bold text-lg border-t pt-2">
                    <span>Grand Total:</span>
                    <span className="text-teal-600">₹{(selectedOrder.grand_total || 0).toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Dispatch Info */}
              {selectedOrder.dispatch_info?.dispatched_at && (
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <h4 className="font-medium text-amber-800 mb-2 flex items-center gap-2">
                    <Truck className="w-4 h-4" /> Dispatch Information
                  </h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600">Vehicle: <strong>{selectedOrder.dispatch_info.vehicle_number}</strong></p>
                      <p className="text-gray-600">Driver: {selectedOrder.dispatch_info.driver_name}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">LR No: {selectedOrder.dispatch_info.lr_number || '-'}</p>
                      <p className="text-gray-600">
                        Dispatched: {formatDate(selectedOrder.dispatch_info.dispatched_at)}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => handlePrintPackingList(selectedOrder)}>
              <ClipboardList className="w-4 h-4 mr-2" /> Packing List
            </Button>
            {selectedOrder?.status !== 'approved' && (
              <Button variant="outline" onClick={() => handlePrintInvoice(selectedOrder)}>
                <Printer className="w-4 h-4 mr-2" /> Print Invoice
              </Button>
            )}
            <Button onClick={() => setShowDetailsDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Invoice Dialog */}
      <Dialog open={showInvoiceDialog} onOpenChange={setShowInvoiceDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-purple-600" /> Create Invoice
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
              <p className="text-sm text-purple-800">
                <strong>Transfer:</strong> {selectedOrder?.transfer_number}
              </p>
              <p className="text-sm text-purple-600">
                <strong>To:</strong> {selectedOrder?.destination_name} ({selectedOrder?.destination_code})
              </p>
              <p className="text-sm text-purple-800 mt-1">
                <strong>Amount:</strong> ₹{(selectedOrder?.grand_total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </p>
            </div>

            <div className="space-y-2">
              <Label>Invoice Number *</Label>
              <Input
                value={invoiceForm.invoice_number}
                onChange={(e) => setInvoiceForm(p => ({ ...p, invoice_number: e.target.value }))}
                placeholder="e.g., SY001-INV-2025-0001"
              />
            </div>

            <div className="space-y-2">
              <Label>Invoice Date</Label>
              <Input
                type="date"
                value={invoiceForm.invoice_date}
                onChange={(e) => setInvoiceForm(p => ({ ...p, invoice_date: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={invoiceForm.notes}
                onChange={(e) => setInvoiceForm(p => ({ ...p, notes: e.target.value }))}
                placeholder="Optional notes..."
                rows={2}
              />
            </div>

            {selectedOrder?.requires_einvoice && (
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-xs text-amber-700 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  E-Invoice will be generated after confirmation
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInvoiceDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleCreateInvoice}
              disabled={processing}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {processing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileCheck className="w-4 h-4 mr-2" />}
              Create Invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dispatch Dialog */}
      <Dialog open={showDispatchDialog} onOpenChange={setShowDispatchDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-amber-600" /> Dispatch Order
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-800">
                <strong>Invoice:</strong> {selectedOrder?.invoice_info?.invoice_number}
              </p>
              <p className="text-sm text-amber-600">
                <strong>To:</strong> {selectedOrder?.destination_name}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Vehicle Number *</Label>
                <Input
                  value={dispatchForm.vehicle_number}
                  onChange={(e) => setDispatchForm(p => ({ ...p, vehicle_number: e.target.value.toUpperCase() }))}
                  placeholder="TN01AB1234"
                />
              </div>
              <div className="space-y-2">
                <Label>LR Number</Label>
                <Input
                  value={dispatchForm.lr_number}
                  onChange={(e) => setDispatchForm(p => ({ ...p, lr_number: e.target.value }))}
                  placeholder="LR-12345"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Driver Name</Label>
                <Input
                  value={dispatchForm.driver_name}
                  onChange={(e) => setDispatchForm(p => ({ ...p, driver_name: e.target.value }))}
                  placeholder="Driver name"
                />
              </div>
              <div className="space-y-2">
                <Label>Driver Phone</Label>
                <Input
                  value={dispatchForm.driver_phone}
                  onChange={(e) => setDispatchForm(p => ({ ...p, driver_phone: e.target.value }))}
                  placeholder="9876543210"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Transporter Name</Label>
              <Input
                value={dispatchForm.transporter_name}
                onChange={(e) => setDispatchForm(p => ({ ...p, transporter_name: e.target.value }))}
                placeholder="Transport company name"
              />
            </div>

            <div className="space-y-2">
              <Label>Expected Delivery Date</Label>
              <Input
                type="date"
                value={dispatchForm.expected_delivery_date}
                onChange={(e) => setDispatchForm(p => ({ ...p, expected_delivery_date: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label>Dispatch Notes</Label>
              <Textarea
                value={dispatchForm.dispatch_notes}
                onChange={(e) => setDispatchForm(p => ({ ...p, dispatch_notes: e.target.value }))}
                placeholder="Any special instructions..."
                rows={2}
              />
            </div>

            {selectedOrder?.requires_eway_bill && (
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-xs text-blue-700 flex items-center gap-1">
                  <FileText className="w-3 h-3" />
                  E-Way Bill required (Value: ₹{(selectedOrder?.grand_total || 0).toLocaleString('en-IN')})
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDispatchDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleDispatch}
              disabled={processing}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {processing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Dispatch
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FranchiseSupplyPage;
